import type { Condition } from '@medplum/fhirtypes'
import { ConditionOrdering } from '@seen/core/models/conditions/ConditionOrdering'
import {
  getReferenceString,
  referenceFromReferenceString,
} from '@seen/core/references'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export interface UseConditionOrderingResult {
  orderedReferenceStrings: string[]
  isLoading: boolean
  updateOrdering: (orderedReferenceStrings: string[]) => void
  isUpdating: boolean
}

export function useConditionOrdering(
  patientReferenceString: string,
  conditionReferenceStrings: string[]
): UseConditionOrderingResult {
  const { data: ordering, isLoading } = useSearchOne(
    ConditionOrdering,
    patientReferenceString
      ? ConditionOrdering.byPatient(
          referenceFromReferenceString(patientReferenceString)
        )
      : undefined
  )

  const { isPending: isCreatingOrdering, mutate: createOrdering } =
    useCreateMutation<ConditionOrdering>()
  const { isPending: isPatchingOrdering, mutate: patchOrdering } =
    usePatchMutation<ConditionOrdering>()
  const [localOrderedIds, setLocalOrderedIds] = useState<string[] | null>(null)

  const storedOrderedReferenceStrings = useMemo(
    () => ordering?.conditions.map(getReferenceString) ?? [],
    [ordering]
  )

  // We want to reset the local ordered ids when the stored ordered reference strings change
  useEffect(() => {
    setLocalOrderedIds(null)
  }, [storedOrderedReferenceStrings])

  const orderedReferenceStrings = useMemo(() => {
    const baseOrder = localOrderedIds ?? storedOrderedReferenceStrings
    const conditionIdSet = new Set(conditionReferenceStrings)
    const orderedFromStore = baseOrder.filter((id) => conditionIdSet.has(id))
    const orderedSet = new Set(orderedFromStore)
    const newConditions = conditionReferenceStrings.filter(
      (id) => !orderedSet.has(id)
    )
    return [...newConditions, ...orderedFromStore]
  }, [
    localOrderedIds,
    storedOrderedReferenceStrings,
    conditionReferenceStrings,
  ])

  const updateOrdering = useCallback(
    (newOrderedReferenceStrings: string[]) => {
      setLocalOrderedIds(newOrderedReferenceStrings)

      if (ordering) {
        patchOrdering({
          instance: ordering,
          mutateFn: (instance) => {
            instance.conditions = newOrderedReferenceStrings.map(
              referenceFromReferenceString<Condition>
            )
          },
        })
      } else {
        const newOrdering = new ConditionOrdering(
          referenceFromReferenceString(patientReferenceString)
        )
        newOrdering.conditions = newOrderedReferenceStrings.map(
          referenceFromReferenceString<Condition>
        )
        createOrdering(newOrdering)
      }
    },
    [ordering, patientReferenceString, patchOrdering, createOrdering]
  )

  return {
    orderedReferenceStrings,
    isLoading,
    updateOrdering,
    isUpdating: isPatchingOrdering || isCreatingOrdering,
  }
}
